import { useState, useRef, useCallback, useEffect } from 'react';

export function useSheetDrag(onClose) {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const contentRef = useRef(null);
  const dragStartY = useRef(0);
  const dragSource = useRef(null);
  const closeTimerRef = useRef(null);

  // Reset state when sheet reopens
  useEffect(() => {
    setDragY(0);
    setIsClosing(false);
  }, [onClose]);

  // Cleanup del timer di chiusura se il componente viene smontato prima
  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsClosing(false);
      setDragY(0);
      onClose();
    }, 250);
  }, [onClose]);

  const handleTouchStart = useCallback((e) => {
    const content = contentRef.current;
    const touch = e.touches[0];
    const contentRect = content?.getBoundingClientRect();
    const touchRelativeY = touch.clientY - (contentRect?.top || 0);
    const isInHandleArea = touchRelativeY < 60;
    const isScrolledToTop = !content || content.scrollTop <= 0;

    if (isInHandleArea) {
      dragSource.current = 'handle';
      dragStartY.current = touch.clientY;
      setIsDragging(true);
      return;
    }

    if (isScrolledToTop) {
      dragSource.current = 'content';
      dragStartY.current = touch.clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const delta = touch.clientY - dragStartY.current;

    if (!isDragging && dragSource.current === 'content') {
      if (delta > 8) {
        setIsDragging(true);
        dragStartY.current = touch.clientY;
        return;
      } else if (delta < -5) {
        dragSource.current = null;
        return;
      }
      return;
    }

    if (!isDragging) return;

    if (delta > 0) {
      e.preventDefault();
    }

    setDragY(delta > 0 ? delta : delta * 0.2);
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) {
      dragSource.current = null;
      return;
    }

    setIsDragging(false);
    dragSource.current = null;

    const containerHeight = contentRef.current?.offsetHeight || 500;
    const threshold = Math.min(120, containerHeight * 0.25);

    if (dragY > threshold) {
      handleClose();
    } else {
      setDragY(0);
    }
  }, [isDragging, dragY, handleClose]);

  const sheetStyle = {
    transform: isClosing ? 'translateY(100%)' : `translateY(${Math.max(0, dragY)}px)`,
    transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.2, 0, 0, 1)',
  };

  const overlayOpacity = isClosing ? 0 : Math.max(0, 0.5 - dragY / 600);

  return {
    contentRef,
    sheetStyle,
    overlayOpacity,
    isDragging,
    isClosing,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleClose,
  };
}
